import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Loader2, LogOut, ShieldCheck, UserCog, WifiOff } from 'lucide-react'
import { useLiveQuery } from 'dexie-react-hooks'

import { useLogout, useSession } from '@/lib/auth'
import { useCanManagePersonnel } from '@/lib/acl'
import { useOnlineStatus } from '@/lib/use-online-status'
import { livePendingEvents } from '@/lib/db'
import { cn } from '@/lib/cn'

const PILL_CLASSES = cn(
  'inline-flex items-center gap-1.5 rounded-pill px-3 py-2 text-xs min-h-[36px]',
  'bg-header-fg/10 border border-header-fg/20 text-header-fg',
  'hover:bg-brand-accent hover:text-[#11161F] hover:border-brand-accent transition',
)

/**
 * Kolejka jest już tylko do opróżnienia (zapisy poprzedniej wersji aplikacji),
 * więc baner nie obiecuje „wrócimy do niej automatycznie" — mówi, co czeka.
 * 1 zapis czeka / 2–4 zapisy czekają / 5+ zapisów czeka.
 */
function pendingSentence(n: number): string {
  if (n === 1) return 'Zaległy zapis z tego urządzenia czeka na wysłanie.'
  const last = n % 10
  const lastTwo = n % 100
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) {
    return `${n} zaległe zapisy z tego urządzenia czekają na wysłanie.`
  }
  return `${n} zaległych zapisów z tego urządzenia czeka na wysłanie.`
}

export default function Header() {
  const { data: user } = useSession()
  const logout = useLogout()
  const location = useLocation()
  const navigate = useNavigate()
  const online = useOnlineStatus()
  const pendingCount = useLiveQuery(() => livePendingEvents().count(), [], 0)
  const canManage = useCanManagePersonnel()

  if (!user) return null

  const isAdminPage = location.pathname.startsWith('/admin')
  const showSyncing = online && pendingCount > 0
  const showEmployeeSwitch = canManage && isAdminPage
  const showAdminSwitch = canManage && !isAdminPage

  async function handleLogout() {
    try {
      await logout.mutateAsync()
    } catch {
      // best-effort; cookie i tak wyczyściło się po stronie serwera, nawigujemy mimo to
    }
    navigate('/login', { replace: true })
  }

  return (
    <>
      <header className="border-b border-header-fg/10 bg-header-bg text-header-fg">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link
            to="/"
            aria-label="Ekran główny pracownika"
            className="flex items-center gap-3 min-w-0 rounded-md -mx-1 px-1 py-1 hover:bg-header-fg/5 transition group"
          >
            <img
              src="/logo.svg"
              alt="Dental Avenue"
              className="h-8 w-auto flex-shrink-0 transition group-hover:opacity-90"
            />
            <span className="text-sm font-medium truncate hidden sm:inline text-header-fg/90 group-hover:text-header-fg transition">
              Czas pracy
            </span>
          </Link>
          <nav className="flex items-center gap-2 text-sm flex-shrink-0">
            {showEmployeeSwitch && (
              <Link to="/" className={PILL_CLASSES}>
                <UserCog className="h-3.5 w-3.5" aria-hidden />
                <span className="hidden sm:inline">Widok pracownika</span>
                <span className="sm:hidden">Pracownik</span>
              </Link>
            )}
            {showAdminSwitch && (
              <Link to="/admin" className={PILL_CLASSES}>
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                <span className="hidden sm:inline">Panel admina</span>
                <span className="sm:hidden">Admin</span>
              </Link>
            )}
            {!online && (
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs',
                  'bg-warning/15 text-warning border border-warning/40',
                )}
                title="Brak połączenia z siecią"
              >
                <WifiOff className="h-3.5 w-3.5" aria-hidden />
                <span>Offline</span>
              </span>
            )}
            {showSyncing && (
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs',
                  'bg-header-fg/10 text-header-fg/80 border border-header-fg/20',
                )}
                title="Wysyłanie zaległych zapisów z tego urządzenia"
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                <span>Synchronizuję {pendingCount}</span>
              </span>
            )}
            <span className="text-header-fg/60 hidden md:inline">{user.name}</span>
            <button
              type="button"
              onClick={handleLogout}
              disabled={logout.isPending}
              aria-label="Wyloguj"
              className="inline-flex items-center gap-1.5 px-2 py-2 min-h-[36px] text-header-fg/70 hover:text-header-fg transition disabled:opacity-50"
            >
              <LogOut className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">Wyloguj</span>
            </button>
          </nav>
        </div>
      </header>
      {/* Treść `text-text-primary`, `warning` zostaje tłu i obramowaniu: para
          warning-na-warning/10 daje w jasnym motywie ok. 2,6:1. */}
      {pendingCount > 0 && (
        <div className="bg-warning/10 border-b border-warning/30 px-4 py-2 text-center text-xs text-text-primary">
          {pendingSentence(pendingCount)}
        </div>
      )}
    </>
  )
}
